import './ForcedOnSafetyCheck.scss';

import React from 'react';
import Tooltip from '@mui/material/Tooltip';

import InfoBanner from 'src/component/UI/InfoBanner';
import { useTranslation } from 'src/i18n';
import { evaluateSetpointSafety, LOWEST_T_CLASS_C, SafetyCheckInput } from 'src/utils/miSetpointCalculator';
import { celsiusToFahrenheitConvertor } from 'src/utils/utils';

interface ForcedOnSafetyCheckProps extends SafetyCheckInput {
  offersApply?: boolean;
}

/**
 * Advisory MI-setpoint safety indicator shown at the bottom of the Forced-On
 * Log modal. It tells the operator whether increasing the setpoint would risk a
 * sheath-temperature violation. It NEVER blocks submission — it is purely
 * informational.
 */
export default function ForcedOnSafetyCheck (props: ForcedOnSafetyCheckProps) {
  const { t } = useTranslation();
  const result = evaluateSetpointSafety(props);

  // The calculator does all math in Celsius (device temps are stored in C).
  // For display, echo the operands back in the operator's preferred unit.
  const isFahrenheit = props.temperatureUnit === 'F';
  const unit = isFahrenheit ? 'F' : 'C';

  const toDisplay = (celsius?: number): number | undefined =>
    isFahrenheit ? celsiusToFahrenheitConvertor(celsius) : celsius;
  const toDisplayDelta = (celsius?: number): number | undefined =>
    celsius === undefined ? undefined : isFahrenheit ? parseFloat((celsius * 9 / 5).toFixed(1)) : celsius;

  if (result.status === 'incomplete') {
    const missing = result.missingFields.map((key) => t(key, { unit })).join(', ');
    const missingDeviceField = result.missingFields.some((key) => key === 'maxSheathTemp' || key === 'ait');
    return (
      <InfoBanner variant='info' className='forced-on-safety-check' data-testid='forced-on-safety-check'>
        {t(props.offersApply ? 'forcedOn.safetyCheckIncompleteApply' : 'forcedOn.safetyCheckIncomplete', { fields: missing })}
        {missingDeviceField && (
          <span className='forced-on-safety-check__advisory'>{t('forcedOn.safetyCheckDeviceFieldHint')}</span>
        )}
      </InfoBanner>
    );
  }

  // AIT is set but below the lowest temperature class — distinct message, no
  // math tooltip (no Area Temp Rating could be derived).
  if (result.aitBelowLowestClass) {
    return (
      <InfoBanner variant='error' className='forced-on-safety-check' data-testid='forced-on-safety-check'>
        {t('forcedOn.safetyCheckAitBelowClass', {
          ait: toDisplay(result.autoIgnitionTemp),
          lowestClass: toDisplay(LOWEST_T_CLASS_C),
          unit,
        })}
        <span className='forced-on-safety-check__advisory'>{t('forcedOn.safetyCheckAdvisory')}</span>
      </InfoBanner>
    );
  }

  const tooltip = (
    <span className='forced-on-safety-check__tooltip'>
      {t('forcedOn.safetyCheckTooltip', {
        maxSheath: toDisplay(result.maxSheathTemp),
        original: toDisplay(result.originalSetPoint),
        forcedOn: toDisplay(result.forcedOnSetPoint),
        safetyFactor: toDisplayDelta(result.safetyFactor),
        projected: toDisplay(result.projectedSheathTemp),
        rating: toDisplay(result.areaTempRating),
        unit,
      })}
    </span>
  );

  if (result.status === 'pass') {
    return (
      <Tooltip title={tooltip} placement='top'>
        <InfoBanner variant='success' className='forced-on-safety-check' data-testid='forced-on-safety-check'>
          {t('forcedOn.safetyCheckPass')}
        </InfoBanner>
      </Tooltip>
    );
  }

  // fail — red, but explicitly advisory (submission is not blocked)
  return (
    <Tooltip title={tooltip} placement='top'>
      <InfoBanner variant='error' className='forced-on-safety-check' data-testid='forced-on-safety-check'>
        {t('forcedOn.safetyCheckFail')}
        <span className='forced-on-safety-check__advisory'>{t('forcedOn.safetyCheckAdvisory')}</span>
      </InfoBanner>
    </Tooltip>
  );
}
